import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MarketingPage } from "@/components/Marketing";
import { POSTS } from "@/lib/content";
import { useSeo } from "@/lib/useSeo";

export function BlogIndex() {
  useSeo("Pet care guides", "Practical guides for multi-pet households: medications, sitters, flea and heartworm schedules.");
  return (
    <MarketingPage>
      <div className="max-w-4xl mx-auto px-5 py-14">
        <h1 className="font-heading text-5xl font-extrabold">Pet care guides</h1>
        <p className="text-slate-600 mt-3 mb-10">Practical, no-fluff guides for busy multi-pet households.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {POSTS.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="pl-card p-6 hover:-translate-y-1 transition-transform" data-testid={`blog-card-${p.slug}`}>
              <h2 className="font-heading text-2xl font-bold">{p.title}</h2>
              <p className="text-slate-600 mt-2">{p.description}</p>
              <span className="text-[#D97706] font-semibold mt-4 inline-flex items-center gap-1">Read <ArrowRight className="h-4 w-4" /></span>
            </Link>
          ))}
          <Link to="/pet-sitter-checklist" className="pl-card p-6 bg-[#FFF7E6]" data-testid="blog-card-checklist">
            <h2 className="font-heading text-2xl font-bold">Free printable pet sitter checklist</h2>
            <p className="text-slate-600 mt-2">Print it, fill it in, stick it on the fridge.</p>
          </Link>
        </div>
      </div>
    </MarketingPage>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  useSeo(post?.title, post?.description);
  if (!post) return <Navigate to="/blog" replace />;
  return (
    <MarketingPage>
      <article className="max-w-2xl mx-auto px-5 py-14 prose-pl" data-testid="blog-post">
        <Link to="/blog" className="text-sm font-semibold text-[#D97706]">← All guides</Link>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold mt-4 mb-6 text-[#1E293B]">{post.title}</h1>
        {post.body.map(([type, content], i) => {
          if (type === "h") return <h2 key={i}>{content}</h2>;
          if (type === "ul") return <ul key={i}>{content.map((x) => <li key={x}>{x}</li>)}</ul>;
          if (type === "cta") return (
            <div key={i} className="pl-card p-6 bg-[#FFF7E6] mt-10 not-prose">
              <p className="font-heading text-xl font-bold mb-4">{content}</p>
              <Link to="/signup" className="pl-btn-primary" data-testid="blog-cta-btn">Start free trial</Link>
            </div>
          );
          return <p key={i}>{content}</p>;
        })}
        <p className="text-xs text-slate-400 mt-8">This article is general information, not veterinary advice. For any health question, please contact your veterinarian.</p>
      </article>
    </MarketingPage>
  );
}
